/**
 * Inicjacja OAuth2 (Authorization Code + PKCE) do Cyberspace Portal.
 *
 * Dwa zastosowania jednego providera:
 *  - purpose "login"  → federacyjne logowanie/rejestracja organizatora (scope openid email)
 *  - purpose "verify" → weryfikacja tożsamości głosującego / „Zielona Aura" (scope openid verification)
 *
 * Provider (po stronie Cyberspace/WordPress) jeszcze nie żyje — funkcja jest sterowana env:
 *   REACT_APP_CYBERSPACE_OAUTH_URL   (endpoint /authorize)
 *   REACT_APP_CYBERSPACE_CLIENT_ID
 * Brak configu → ostrzeżenie w konsoli (zamiast redirectu na zepsuty URL).
 */

const OAUTH_URL = process.env.REACT_APP_CYBERSPACE_OAUTH_URL;
const CLIENT_ID = process.env.REACT_APP_CYBERSPACE_CLIENT_ID;

const SCOPES: Record<CyberspacePurpose, string> = {
  login: "openid email",
  verify: "openid verification"
};

export type CyberspacePurpose = "login" | "verify";

const SS_KEYS = {
  state: "cs_oauth_state",
  verifier: "cs_oauth_verifier",
  purpose: "cs_oauth_purpose",
  redirect: "cs_oauth_redirect"
} as const;

function base64url(bytes: Uint8Array): string {
  let str = "";
  bytes.forEach(b => (str += String.fromCharCode(b)));
  return btoa(str).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function randomString(byteLength = 48): string {
  const bytes = new Uint8Array(byteLength);
  crypto.getRandomValues(bytes);
  return base64url(bytes);
}

async function pkceChallenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  return base64url(new Uint8Array(digest));
}

interface StartOptions {
  purpose: CyberspacePurpose;
  /** dokąd wrócić po udanym powrocie (np. ścieżka głosowania) */
  redirectPath?: string;
}

export async function startCyberspaceOAuth({ purpose, redirectPath }: StartOptions): Promise<void> {
  if (!OAUTH_URL || !CLIENT_ID) {
    // eslint-disable-next-line no-console
    console.warn(
      "[cyberspace] OAuth nie skonfigurowany — ustaw REACT_APP_CYBERSPACE_OAUTH_URL i REACT_APP_CYBERSPACE_CLIENT_ID"
    );
    return;
  }

  const state = randomString(24);
  const verifier = randomString(48);
  const challenge = await pkceChallenge(verifier);

  sessionStorage.setItem(SS_KEYS.state, state);
  sessionStorage.setItem(SS_KEYS.verifier, verifier);
  sessionStorage.setItem(SS_KEYS.purpose, purpose);
  if (redirectPath) sessionStorage.setItem(SS_KEYS.redirect, redirectPath);

  const params = new URLSearchParams({
    response_type: "code",
    client_id: CLIENT_ID,
    redirect_uri: `${process.env.REACT_APP_URL}/auth/cyberspace/callback`,
    scope: SCOPES[purpose],
    state,
    code_challenge: challenge,
    code_challenge_method: "S256"
  });

  window.location.href = `${OAUTH_URL}?${params.toString()}`;
}

/** Odczyt + walidacja zapisanego stanu w callbacku (anty-CSRF). Zwraca null gdy niezgodny. */
export function consumeCyberspaceCallbackState(returnedState: string | null) {
  const state = sessionStorage.getItem(SS_KEYS.state);
  const verifier = sessionStorage.getItem(SS_KEYS.verifier);
  const purpose = sessionStorage.getItem(SS_KEYS.purpose) as CyberspacePurpose | null;
  const redirect = sessionStorage.getItem(SS_KEYS.redirect);

  Object.values(SS_KEYS).forEach(k => sessionStorage.removeItem(k));

  if (!returnedState || !state || returnedState !== state || !verifier) return null;
  return { verifier, purpose, redirect };
}
